import type { FC, ReactNode } from 'react'

import { ArrowUpRight, ShieldCheck } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { PageHero } from '@/components/generals/page-hero'
import { ButtonLink } from '@/components/generals/button'
import { CtaBand } from '@/components/generals/cta-band'
import { TransitionLink } from '@/components/generals/transition-link'
import { ImageReveal } from '@/components/motion/image-reveal'
import { Reveal } from '@/components/motion/reveal'
import { ServiceIndexList } from '@/components/widgets/service-index-list'
import { siteConfig, QUOTE_PATH } from '@/constants/site'

const ServicesPage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Services'
                description="Garage door repair and installation across the Riverton metro: emergencies, springs, openers, cables, panels, new doors, commercial doors and tune-ups."
            />

            <PageHero
                eyebrow="What we do"
                title="From a snapped spring to a brand-new door."
                accent={['brand-new', 'door.']}
                description="We check 21 safety points on every visit and hand you a written price before we start. Open any service for the steps involved, the usual time on site and the typical cost."
                actions={(
                    <>
                        <ButtonLink href={QUOTE_PATH} size='lg'>Request a price</ButtonLink>
                        <ButtonLink href={siteConfig.phoneHref} variant='outline' size='lg' icon='phone'>Call {siteConfig.phone}</ButtonLink>
                    </>
                )}
                aside={(
                    <div className='relative'>
                        <ImageReveal
                            src='/images/services/panel-replacement.jpg'
                            alt="Two technicians lifting a new door panel into place"
                            className='aspect-[4/5] rounded-[32px]'
                            sizes='(min-width: 1024px) 38vw, 100vw'
                            reveal={false}
                            priority
                        />

                        <div className='absolute -bottom-6 left-6 right-6 flex items-center gap-4 rounded-[22px] bg-ink p-5 text-cream shadow-[0_30px_60px_-30px_rgba(21,23,26,0.7)] sm:left-auto sm:w-80'>
                            <span className='flex size-11 shrink-0 items-center justify-center rounded-full bg-ember text-white'>
                                <ShieldCheck className='size-5' aria-hidden='true' />
                            </span>

                            <p className='text-[14.5px] leading-snug'>
                                Every visit: a 21-point safety check, then a price in writing before any work.
                            </p>
                        </div>
                    </div>
                )}
            />

            <section className='pb-20 pt-10 md:pb-28'>
                <Container>
                    <ServiceIndexList />

                    <Reveal className='mt-12'>
                        <TransitionLink
                            href='/#diagnose'
                            className='group flex flex-col gap-4 rounded-[28px] bg-sand/70 p-6 transition-colors hover:bg-sand sm:flex-row sm:items-center sm:justify-between sm:p-8'
                        >
                            <p className='font-heading text-[1.35rem] font-semibold leading-snug tracking-[-0.025em] sm:text-[1.6rem]'>
                                Can't tell what the door needs? Describe the symptoms in the 30-second diagnosis on the home page.
                            </p>

                            <span className='inline-flex shrink-0 items-center gap-2 text-[15px] font-medium text-ember'>
                                Start the diagnosis
                                <ArrowUpRight className='size-4' aria-hidden='true' />
                            </span>
                        </TransitionLink>
                    </Reveal>
                </Container>
            </section>

            <CtaBand
                title="Know the price before anyone picks up a tool."
                body="Describe the door in a few lines. A price range comes back by text, usually within 30 minutes."
                primary={{ label: "Request a price", href: QUOTE_PATH }}
            />
        </Layout>
    )
}

export default ServicesPage
